import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  FC,
} from 'react';

import { ThemeColors } from 'styled-components';

import { ConfirmationModal } from '../components/ConfirmationModal';

type AsyncFunction = () => Promise<void>;
type RegularFunction = () => void;
type OnChoiseFunction = AsyncFunction | RegularFunction;
type TCustomOptions = {
  cancel: string;
  confirm: string;
};

interface OpenRequest {
  content: ReactNode;
  title?: string;
  subtitle?: string;
  options?: TCustomOptions;
  size?: string;
  titleColor?: ThemeColors;
  onConfirm?: OnChoiseFunction;
  onCancel?: OnChoiseFunction;
}

interface IConfirmationModalContextData {
  open(request: OpenRequest): void;
  close(): void;
  onConfirm: OnChoiseFunction;
  onCancel?: OnChoiseFunction;
  isOpen: boolean;
  options: TCustomOptions;
  size: string;
}

const ConfirmationModalContext = createContext(
  {} as IConfirmationModalContextData
);

const ConfirmationModalProvider: FC = ({ children }) => {
  const [content, setContent] = useState<ReactNode>('');
  const [subtitle, setSubtitle] = useState<string>('');
  const [onConfirm, setOnConfirm] = useState(() => null);
  const [onCancel, setOnCancel] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [titleColor, setTitleColor] = useState<ThemeColors>();
  const [size, setSize] = useState<string>('310px');
  const [options, setOptions] = useState<TCustomOptions>({
    cancel: 'não',
    confirm: 'sim',
  });

  const open = useCallback((request: OpenRequest) => {
    setContent(request.content);
    setSubtitle(request.subtitle);
    setTitleColor(request.titleColor);
    setOnConfirm(() => request.onConfirm);
    setOnCancel(() => request.onCancel);
    setIsOpen(true);

    if (request.size) {
      setSize(request.size);
    }

    if (request.options) {
      setOptions(request.options);
    }
  }, []);

  const close = useCallback(() => {
    setIsOpen(false);
    setOnConfirm(() => null);
    setContent('');
  }, []);

  return (
    <ConfirmationModalContext.Provider
      value={{ open, close, size, onConfirm, onCancel, isOpen, options }}
    >
      {children}

      {isOpen && (
        <ConfirmationModal
          content={content}
          subtitle={subtitle}
          titleColor={titleColor}
        />
      )}
    </ConfirmationModalContext.Provider>
  );
};

const useConfirmationModal = (): IConfirmationModalContextData => {
  return useContext(ConfirmationModalContext);
};

export { ConfirmationModalProvider, useConfirmationModal };
